import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Card, CardContent,
  Checkbox, FormControlLabel, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { API_URL } from '../utils/api';
import { trackEvent } from '../utils/analytics';
import { COLORS } from '../utils/colors';

const EarlyAccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountExists, setAccountExists] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim() || !acceptedTerms) return;
    setLoading(true);
    setError('');
    setAccountExists(false);
    try {
      const res = await fetch(`${API_URL}/early-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: 'Early Access Request — ' + name.trim(),
          message: message.trim() || 'Requesting early access to Percoro.',
        }),
      });
      const data = await res.json();
      if (data.success) {
        trackEvent('early_access_requested');
        setSubmitted(true);
      } else if (data.error === 'account_exists') {
        setAccountExists(true);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Card sx={{ maxWidth: 480, width: '100%', borderRadius: 4, boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{ bgcolor: COLORS.primary, borderRadius: '16px 16px 0 0', px: 4, pt: 4, pb: 3, textAlign: 'center' }}>
          <Typography
            onClick={() => navigate('/')}
            sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '1.5rem', color: 'white', mb: 0.5, cursor: 'pointer', display: 'inline-block' }}
          >
            PERCORO
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem', fontWeight: 500 }}>
            Join Early Access
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {submitted ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 56, mb: 2 }} />
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                Request Received!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                Thanks for your interest in Percoro. We'll be in touch shortly with your early access invitation.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{ borderColor: COLORS.primary, color: COLORS.primary, borderRadius: '24px', textTransform: 'none', fontWeight: 600 }}
              >
                Back to Percoro
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                Percoro is currently in private early access. Request an invitation and we'll get you set up.
              </Typography>

              {accountExists && (
                <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#EAF5F3', border: '1px solid ' + COLORS.primary }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.primary, mb: 0.5 }}>
                    You already have a Percoro account
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.5 }}>
                    There's no need to request early access. Log in to your account, or reset your password if you've forgotten it.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" onClick={() => navigate('/login')}
                      sx={{ bgcolor: COLORS.primary, textTransform: 'none', borderRadius: '20px', fontWeight: 600, '&:hover': { bgcolor: COLORS.primaryDark } }}>
                      Log in
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => navigate('/forgot-password')}
                      sx={{ borderColor: COLORS.primary, color: COLORS.primary, textTransform: 'none', borderRadius: '20px', fontWeight: 600 }}>
                      Reset password
                    </Button>
                  </Box>
                </Box>
              )}

              {error && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>
              )}

              <TextField
                fullWidth size="small" label="Your Name"
                value={name} onChange={(e) => setName(e.target.value)}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth size="small" label="Email Address" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth size="small" label="What are you most excited to try? (optional)"
                value={message} onChange={(e) => setMessage(e.target.value)}
                multiline rows={2}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    sx={{ color: COLORS.primary, '&.Mui-checked': { color: COLORS.primary }, pt: 0 }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    I understand this is an early access platform and the service may be discontinued at any time.
                  </Typography>
                }
                sx={{ mb: 3, alignItems: 'flex-start' }}
              />

              <Button
                fullWidth variant="contained" onClick={handleSubmit}
                disabled={loading || !name.trim() || !email.trim() || !email.includes('@') || !acceptedTerms}
                sx={{
                  bgcolor: COLORS.primary, borderRadius: '24px', textTransform: 'none',
                  fontWeight: 700, py: 1.3, fontSize: '1rem',
                  '&:hover': { bgcolor: COLORS.primaryDark },
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Request Early Access'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="text" onClick={() => navigate('/')}
                  sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}
                >
                  Back to Percoro
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EarlyAccessPage;
